import { alpha, type SxProps, type Theme } from '@mui/material/styles'

import {
  CELEBRATION_GRADIENT,
  WARM_LIME_ACCENT_GRADIENT,
} from '../../../components/celebrationGradients'

export interface BlockLayoutConfig {
  background?: string
  logo_color?: string
  logo_bGcolor?: string
  logo_border_color?: string
  /** mx-icons glyph shown before `sub_text` (e.g. a warning icon on TransparentOverdraft). */
  sub_logo?: string
  /** When true, AccountBlock renders the confetti-backed celebration card using the fields below. */
  isConfetti?: boolean
  /** Gradient wash layered over `background.paper` for the confetti card. */
  gradient?: string
  /** Which illustration sits left of the text. `logo` is the data-driven merchant/institution logo. */
  graphic?: 'paidOffCard' | 'savings' | 'logo'
  /** Amount typography variant — `h2` for milestones, `h3` for deposits. */
  amountVariant?: 'h2' | 'h3'
  /** Prefix prepended to the formatted amount, e.g. `+` for deposits. */
  amountPrefix?: string
  /** Render the colored category strip (`header_text`) at the top of the card. */
  categoryStrip?: boolean
  /** Layout overrides for the graphic+text row (padding, gap). */
  rowSx?: SxProps
  /** Which payload field feeds the secondary line beside the amount. */
  secondarySource?: 'merchant' | 'label'
  /** Whether the secondary line sits above or below the amount. */
  secondaryPosition?: 'above' | 'below'
  /** Typography variant for the secondary line. */
  secondaryVariant?: 'body2' | 'caption'
  /** Theme color path for the secondary line. */
  secondaryColor?: string
}

export const getLayoutConfig = (theme: Theme): Record<string, BlockLayoutConfig> => {
  return {
    TransparentOverdraft: {
      background: theme.palette.warning.light,
      sub_logo: 'error',
    },
    LowAccountBalance: {
      background:
        'linear-gradient(256.82deg, rgba(255, 47, 40, 0.2) 1.86%, rgba(255, 0, 72, 0.2) 101.54%)',
    },
    MonthlyObligationsStatusOffTrack: {
      logo_color: 'background.paper',
      logo_bGcolor: 'error.main',
      background:
        'linear-gradient(256.82deg, rgba(255, 47, 40, 0.2) 1.86%, rgba(255, 0, 72, 0.2) 101.54%)',
    },
    MonthlyCreditCardInterestSummary: {
      logo_color: 'text.primary',
      logo_bGcolor: alpha(theme.palette.categories?.feesCharges || '', 0.25),
      logo_border_color: 'categories.feesCharges',
      background:
        'linear-gradient(256.82deg, rgba(227, 39, 39, 0.2) 1.86%, rgba(255, 123, 0, 0.2) 101.54%)',
    },
    UnifiedDeposit: {
      isConfetti: true,
      gradient: WARM_LIME_ACCENT_GRADIENT,
      graphic: 'logo',
      amountVariant: 'h3',
      amountPrefix: '+',
      categoryStrip: true,
      rowSx: { gap: 12, p: 12 },
      secondarySource: 'merchant',
      secondaryPosition: 'below',
      secondaryVariant: 'body2',
      secondaryColor: 'text.primary',
    },
    UnifiedDepositEmergencyFund: {
      isConfetti: true,
      gradient: WARM_LIME_ACCENT_GRADIENT,
      graphic: 'logo',
      amountVariant: 'h3',
      amountPrefix: '+',
      categoryStrip: true,
      rowSx: { gap: 12, p: 12 },
      secondarySource: 'merchant',
      secondaryPosition: 'below',
      secondaryVariant: 'body2',
      secondaryColor: 'text.primary',
    },
    MonthlyObligationsStatusOnTrack: {
      isConfetti: true,
      gradient: WARM_LIME_ACCENT_GRADIENT,
      graphic: 'logo',
      amountVariant: 'h2',
      rowSx: { gap: 16, p: 16 },
      secondarySource: 'label',
      secondaryPosition: 'below',
      secondaryVariant: 'caption',
      secondaryColor: 'text.secondary',
    },
    CelebrateCreditCardPayoff: {
      isConfetti: true,
      gradient: CELEBRATION_GRADIENT,
      graphic: 'paidOffCard',
      amountVariant: 'h2',
      rowSx: { gap: 24, px: 24, py: 16 },
      secondarySource: 'label',
      secondaryPosition: 'below',
      secondaryVariant: 'caption',
      secondaryColor: 'text.secondary',
    },
    SavingsAccountDeposit: {
      isConfetti: true,
      gradient: CELEBRATION_GRADIENT,
      graphic: 'savings',
      amountVariant: 'h2',
      rowSx: { gap: 16, p: 16 },
      secondarySource: 'label',
      secondaryPosition: 'above',
      secondaryVariant: 'body2',
      secondaryColor: 'text.primary',
    },
  }
}
